import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { OFFICIAL_VAULTS_NS as NS } from './constants';
import { formatUsdc } from '../vaults/helpers';
import { ApplicationView, StakeNotice, isUnappliedApproval } from './helpers';
import type { ApproveForm } from './useOfficialVaults';

/**
 * Approve an application (STORY-22 T-07; D-30 amendment 2026-10-02, Option B): the vault opens ACTIVE — no raise window,
 * the button reads "Approve". Approval is the only way a vault opens (AC15). A refusal keeps the dialog, shows why and
 * offers Retry; the application stays Pending.
 *
 * An approval that did not open the vault is not replaced by approving again: it is the same decision, so the engine
 * replays it and retries the open, with no quiet period (ea review, 2026-10-02). The replace countdown belongs to the
 * reject dialog only.
 */
export const ApproveDialog = ({
  form,
  stake = null,
  request = null,
}: {
  form: Pick<ApproveForm, 'vaultId' | 'alert' | 'submitting' | 'closeDialog' | 'submit'>;
  /** What to say about the manager's stake; null when it is in. Warns, never blocks — the engine re-checks it. */
  stake?: StakeNotice | null;
  /** The application under review: says when approving again retries an approval that did not open the vault. */
  request?: Pick<ApplicationView, 'review' | 'vaultState'> | null;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');

  return (
    <Dialog
      open={form.vaultId !== null}
      onClose={form.submitting ? undefined : form.closeDialog}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      <DialogTitle>{translate(`${NS}.review.approveTitle`, { vaultId: form.vaultId })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">{translate(`${NS}.review.immutableWarning`)}</Alert>
          {request && isUnappliedApproval(request) && (
            <Alert severity="info">{translate(`${NS}.review.approveRetriesOpen`)}</Alert>
          )}
          {stake && (
            <Alert severity="warning">
              {stake.kind === 'short'
                ? translate(`${NS}.review.stakeShort`, {
                    received: formatUsdc(stake.received),
                    expected: formatUsdc(stake.expected),
                  })
                : translate(
                    `${NS}.review.${stake.kind === 'rejected' ? 'stakeRejected' : 'stakeUnconfirmed'}`
                  )}
            </Alert>
          )}
          {form.alert && (
            <Alert severity={form.alert.severity}>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {form.alert.messages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={form.closeDialog} disabled={form.submitting}>
          {translate(`${NS}.form.cancel`)}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={form.submit}
          disabled={form.submitting}
          loading={form.submitting}
        >
          {/* A refused approve leaves the application Pending, shows why, and offers Retry (STORY-22 T-07). */}
          {translate(
            form.alert?.severity === 'error' ? `${NS}.review.retry` : `${NS}.review.approve`
          )}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
